import { Html, Line } from '@react-three/drei'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { BufferAttribute, BufferGeometry, DoubleSide, Vector3 } from 'three'
import { KVInspectionArea } from 'types'

import {
  ANNOTATION_Y_OFFSET,
  AxisConfig,
  AxisSource,
} from './utils/annotations'

const InspectionAreaLabel = styled.div`
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 10px;
  pointer-events: none;
  user-select: none;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  line-height: 1.4;
`

const RCIValue = styled.span<{ $rci: number }>`
  color: ${({ $rci }) => {
    const percentAffected = 100 - $rci
    if (percentAffected > 45) return '#dc2626'
    if (percentAffected > 35) return '#ea580c'
    if (percentAffected > 20) return '#d97706'
    return '#22c55e'
  }};
`

const AreaText = styled.span`
  font-size: 10px;
  font-weight: 400;
  opacity: 0.85;
  margin-left: 5px;
`

export type AreaUnit = 'm' | 'ft'

/** Conversion factor: 1 m² = 10.7639 ft² */
const SQ_METERS_TO_SQ_FEET = 10.7639

/** Format area with unit conversion */
function formatArea(areaCm2: number, unit: AreaUnit): string {
  // Convert from cm² to m²
  let areaM2 = areaCm2 / 10000

  // Convert to ft² if needed
  if (unit === 'ft') {
    areaM2 = areaM2 * SQ_METERS_TO_SQ_FEET
  }

  return areaM2.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

/**
 * Parse vertex position - handles multiple formats:
 * 1. String format "X=-123.45 Y=67.89 Z=-10.11"
 * 2. WorldPosition object { x, y, z }
 * 3. Vertex object with vertexPosition string
 */
function parseVertexPosition(
  vertex: unknown
): { x: number; y: number; z: number } | null {
  if (!vertex) return null

  // Handle WorldPosition object directly (has x, y, z properties)
  if (
    typeof vertex === 'object' &&
    'x' in vertex &&
    'y' in vertex &&
    'z' in vertex
  ) {
    // Type guard confirmed object has x, y, z properties
    const pos = vertex as { x: number; y: number; z: number }
    const x = Number(pos.x)
    const y = Number(pos.y)
    const z = Number(pos.z)
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      return { x, y, z }
    }
  }

  // Handle Vertex object with vertexPosition string
  if (typeof vertex === 'object' && 'vertexPosition' in vertex) {
    // Type guard confirmed object has vertexPosition property
    const vertexPosition = (vertex as { vertexPosition: string }).vertexPosition
    if (vertexPosition && typeof vertexPosition === 'string') {
      const match = vertexPosition.match(
        /X=([-\d.]+)\s+Y=([-\d.]+)\s+Z=([-\d.]+)/
      )
      if (match) {
        const x = parseFloat(match[1])
        const y = parseFloat(match[2])
        const z = parseFloat(match[3])
        if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
          return { x, y, z }
        }
      }
    }
  }

  return null
}

function getRCIColor(rci: number): string {
  const percentAffected = 100 - rci
  if (percentAffected > 45) return '#dc2626'
  if (percentAffected > 35) return '#ea580c'
  if (percentAffected > 20) return '#d97706'
  return '#22c55e'
}

interface InspectionAreaMarkerProps {
  inspectionArea: KVInspectionArea
  axisConfig: AxisConfig
  showLabels: boolean
  showArea: boolean
  areaUnit: AreaUnit
  rci?: number
}

/**
 * Renders an inspection area as a translucent filled polygon
 * with optional RCI-based coloring
 */
const InspectionAreaMarker: FC<InspectionAreaMarkerProps> = ({
  inspectionArea,
  axisConfig,
  showLabels,
  showArea,
  areaUnit,
  rci,
}) => {
  // Parse vertices and convert to Three.js coordinates
  const { vectors, center, labelPosition } = useMemo(() => {
    // Configurable axis mapping with Y offset to float above mesh
    const mapToThree = (p: { x: number; y: number; z: number }): Vector3 => {
      const getValue = (axis: AxisSource) => {
        switch (axis) {
          case 'x':
            return p.x
          case 'y':
            return p.y
          case 'z':
            return p.z
        }
      }
      return new Vector3(
        getValue(axisConfig.xFrom) * axisConfig.xSign,
        getValue(axisConfig.yFrom) * axisConfig.ySign - ANNOTATION_Y_OFFSET,
        getValue(axisConfig.zFrom) * axisConfig.zSign
      )
    }

    if (!inspectionArea.vertices || inspectionArea.vertices.length < 3) {
      return { vectors: [], center: null, labelPosition: null }
    }

    const points = inspectionArea.vertices
      .map((v) => parseVertexPosition(v))
      .filter((p): p is { x: number; y: number; z: number } => p !== null)

    if (points.length < 3) {
      return { vectors: [], center: null, labelPosition: null }
    }

    const vecs = points.map(mapToThree)

    // Calculate center
    const c = new Vector3()
    vecs.forEach((v) => c.add(v))
    c.divideScalar(vecs.length)

    // Find the top edge (edge with highest average Y) and place label at its midpoint
    let topEdgeMidpoint = vecs[0].clone()
    let highestAvgY = -Infinity

    for (let i = 0; i < vecs.length; i++) {
      const v1 = vecs[i]
      const v2 = vecs[(i + 1) % vecs.length]
      const avgY = (v1.y + v2.y) / 2

      if (avgY > highestAvgY) {
        highestAvgY = avgY
        topEdgeMidpoint = v1.clone().add(v2).multiplyScalar(0.5)
      }
    }

    return { vectors: vecs, center: c, labelPosition: topEdgeMidpoint }
  }, [inspectionArea.vertices, axisConfig])

  // Create geometry for the filled polygon
  const geometry = useMemo(() => {
    if (vectors.length < 3) return null

    // Create a BufferGeometry with triangulated vertices
    // Using a simple fan triangulation from the center
    const geo = new BufferGeometry()
    const positions: number[] = []
    const indices: number[] = []

    // Add all vertices
    vectors.forEach((v) => {
      positions.push(v.x, v.y, v.z)
    })

    // Create triangles using fan triangulation from first vertex
    for (let i = 1; i < vectors.length - 1; i++) {
      indices.push(0, i, i + 1)
    }

    geo.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(positions), 3)
    )
    geo.setIndex(indices)
    geo.computeVertexNormals()

    return geo
  }, [vectors])

  if (vectors.length < 3 || !center || !labelPosition || !geometry) {
    return null
  }

  const labelText =
    inspectionArea.label || `Area ${inspectionArea.id.slice(0, 8)}`

  const fillColor = rci !== undefined ? getRCIColor(rci) : '#808080'
  const outlineColor = rci !== undefined ? '#ffffff' : '#a0a0a0'
  const fillOpacity = rci !== undefined ? 0.4 : 0.3

  return (
    <group>
      {/* Filled polygon */}
      <mesh geometry={geometry}>
        <meshStandardMaterial
          color={fillColor}
          transparent
          opacity={fillOpacity}
          side={DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {/* Outline */}
      <Line
        points={[...vectors, vectors[0]]}
        color={outlineColor}
        lineWidth={2}
      />

      {/* Label - positioned at center of area for better visibility */}
      {showLabels && (
        <Html position={center} center>
          <InspectionAreaLabel>
            {labelText}
            {rci !== undefined && (
              <>
                <br />
                RCI: <RCIValue $rci={rci}>{rci.toFixed(0)}</RCIValue>
              </>
            )}
            {rci === undefined && showArea && inspectionArea.area && (
              <AreaText>
                {formatArea(inspectionArea.area, areaUnit)}{' '}
                {areaUnit === 'm' ? 'm²' : 'ft²'}
              </AreaText>
            )}
          </InspectionAreaLabel>
        </Html>
      )}
    </group>
  )
}

interface ReadOnly3DTilesInspectionAreasProps {
  inspectionAreas: KVInspectionArea[]
  axisConfig: AxisConfig
  showLabels?: boolean
  showArea?: boolean
  areaUnit?: AreaUnit
  rciValues?: Map<string, number>
  ignoreVisibility?: boolean
}

export const ReadOnly3DTilesInspectionAreas: FC<
  ReadOnly3DTilesInspectionAreasProps
> = ({
  inspectionAreas,
  axisConfig,
  showLabels = true,
  showArea = true,
  areaUnit = 'm',
  rciValues,
  ignoreVisibility = false,
}) => {
  const visibleAreas = ignoreVisibility
    ? inspectionAreas
    : inspectionAreas.filter((ia) => ia.visible !== false)

  if (visibleAreas.length === 0) {
    return null
  }

  return (
    <group>
      {visibleAreas.map((inspectionArea, index) => (
        <InspectionAreaMarker
          key={`${inspectionArea.id}-${index}`}
          inspectionArea={inspectionArea}
          axisConfig={axisConfig}
          showLabels={showLabels}
          showArea={showArea}
          areaUnit={areaUnit}
          rci={rciValues?.get(inspectionArea.id)}
        />
      ))}
    </group>
  )
}
